import React, { useState, useEffect } from 'react'
import { X, Save } from 'lucide-react'

interface ColumnEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (columnName: string, columnType: string) => void
  initialName?: string
  initialType?: string
  title: string
}

export const ColumnEditModal: React.FC<ColumnEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialName = '',
  initialType = 'TEXT',
  title
}) => {
  const [columnName, setColumnName] = useState(initialName)
  const [columnType, setColumnType] = useState(initialType)

  useEffect(() => {
    if (isOpen) {
      setColumnName(initialName)
      setColumnType(initialType)
    }
  }, [isOpen, initialName, initialType])

  const handleSave = () => {
    if (columnName.trim()) {
      onSave(columnName.trim(), columnType)
      onClose()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              列名
            </label>
            <input
              type="text"
              value={columnName}
              onChange={(e) => setColumnName(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="列名を入力"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              データ型
            </label>
            <select
              value={columnType}
              onChange={(e) => setColumnType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="TEXT">TEXT (テキスト)</option>
              <option value="INTEGER">INTEGER (整数)</option>
              <option value="REAL">REAL (小数)</option>
              <option value="BOOLEAN">BOOLEAN (真偽値)</option>
              <option value="DATETIME">DATETIME (日時)</option>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            キャンセル
          </button>
          <button
            onClick={handleSave}
            disabled={!columnName.trim()}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="h-4 w-4" />
            <span>保存</span>
          </button>
        </div>
      </div>
    </div>
  )
} 